import { $alert } from '@/plugins/simple-alert'

interface SortableObject {
  [key: string]: unknown
}

const commonUtil = {
  checkStringLength(val: string, target: string, paramlength: number): boolean {
    if (val && val.split('').length > paramlength) {
      $alert.warn(`Please ${target} sould be ${paramlength}`)
      return false
    }
    return true
  },

  checkUndefinedOrEmptyString(val: unknown, target: string, alert = true): boolean {
    if (typeof val == 'undefined' || String(val).trim() == '') {
      if (alert) {
        $alert.warn('Please enter some value : ' + target)
      }
      return false
    }
    return true
  },

  /**
   * convert seze in bytes to KB, MB, GB, ...
   * @param {any} bytes - byte 크기
   * @param {Number} decimals - 표현할 소수점 자리수
   * @param {Boolean} removeUnit - 표시 단위 제거 여부
   * @param {Number} formatUnitIndex - 단위 number(0:Bytes, 1:KB, 2:MB, 3:GB, 4:TB, 5:PB, 6:EB, 7:ZB, 8:YB)
   */
  formatBytes(
    val: any, // 문자열이나 null 등이 들어올 수 있으므로 any
    decimals?: number,
    removeUnit?: boolean,
    formatUnitIndex?: number,
  ): string {
    if (val === null || val === undefined || val === '' || isNaN(Number(val))) {
      return ''
    }

    let bytes = Number(val)

    if (bytes == 0) {
      return '0'
    }

    const reversNum = bytes > 0 ? 1 : -1 //음수가 들어왔을 경우 log 계산식에서는 양수로 계산하고 return 시 음수로 보정하기 위함
    bytes *= reversNum

    const k = 1024
    const dm = decimals === undefined || decimals <= 0 ? 0 : decimals || 3
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']

    let i = formatUnitIndex ? formatUnitIndex : Math.floor(Math.log(bytes) / Math.log(k))
    i = i == -1 ? 0 : i

    if (removeUnit) {
      return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) * reversNum + ''
    } else {
      return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) * reversNum + ' ' + sizes[i]
    }
  },

  /**
   * convert seze in bytes to KB, MB, GB, ...
   * @param {any} bytes - byte 크기
   * @param {Number} decimals - 표현할 소수점 자리수
   * @param {Boolean} removeUnit - 표시 단위 제거 여부
   * @param {Number} formatUnitIndex - 단위 number(0:Bytes, 1:KB, 2:MB, 3:GB, 4:TB, 5:PB, 6:EB, 7:ZB, 8:YB)
   */
  formatBytes2(
    val: any, // 문자열이나 null 등이 들어올 수 있으므로 any
    decimals?: number,
    removeUnit?: boolean,
    formatUnitIndex?: number,
  ): string {
    if (val === null || val === undefined || val === '' || isNaN(Number(val))) {
      return ''
    }

    const bytes = Number(val)
    const absBytes = Math.abs(bytes)
    const reversNum = bytes < 0 ? -1 : 1

    const k = 1024
    const dm = decimals === undefined || decimals < 0 ? 0 : decimals
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']

    let i = 0
    if (formatUnitIndex !== undefined) {
      i = formatUnitIndex
    } else {
      i = absBytes === 0 ? 0 : Math.floor(Math.log(absBytes) / Math.log(k))
    }

    if (i < 0) i = 0
    if (i >= sizes.length) i = sizes.length - 1

    const calcValue = (absBytes / Math.pow(k, i)) * reversNum
    const formattedValue = calcValue.toFixed(dm)

    if (removeUnit) {
      return formattedValue
    } else {
      return formattedValue + ' ' + sizes[i]
    }
  },

  /**
   * convert seze in number to k, m, b, t, ...
   * @param {Number} val - 크기
   * @param {Number} decimals - 표현할 소수점 자리수
   * @param {Boolean} removeUnit - 표시 단위 제거 여부
   * @param {Number} formatUnitIndex - 단위 number(1:k, 2:m, 3:b, 4:t, 5:quad, 6:quin)
   * @param {Boolean} unitUpper - 단위 number(1:k, 2:m, 3:b, 4:t, 5:quad, 6:quin)
   */
  formatSize(
    val: any, // 문자열이나 null 등이 들어올 수 있으므로 any
    decimals?: number,
    removeUnit?: boolean,
    formatUnitIndex?: number,
    unitUpper = true,
  ): string {
    if (val === null || val === undefined || val === '' || isNaN(Number(val))) {
      return ''
    }

    let numericVal = Number(val)

    if (numericVal == 0) {
      return '0'
    }

    const reversNum = numericVal > 0 ? 1 : -1 //음수가 들어왔을 경우 log 계산식에서는 양수로 계산하고 return 시 음수로 보정하기 위함
    numericVal *= reversNum

    const k = 1000
    const dm = decimals === undefined || decimals <= 0 ? 0 : decimals || 3
    const sizes = ['', 'k', 'm', 'b', 't']

    let i = formatUnitIndex ? formatUnitIndex : Math.floor(Math.log(numericVal) / Math.log(k))
    if (i >= sizes.length) i = sizes.length - 1
    i = i < 0 ? 0 : i

    const unitStr = sizes[i] || ''
    const unit = unitUpper ? unitStr.toUpperCase() : unitStr
    if (removeUnit) {
      return parseFloat((numericVal / Math.pow(k, i)).toFixed(dm)) * reversNum + ''
    } else {
      return parseFloat((numericVal / Math.pow(k, i)).toFixed(dm)) * reversNum + ' ' + unit
    }
  },

  /**
   * Byte 단위의 크기를 적절한 단위의 크기로 환산하기 index number
   * 'Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'
   * @param {Number} bytes
   */
  getFormatUnitIndex(bytes: number): number {
    const k = 1024
    const i = Math.floor(Math.log(Math.abs(bytes)) / Math.log(k))
    return i
  },

  /**
   * 숫자 단위의 크기를 적절한 단위의 크기로 환산하기 index number
   * 'k', 'm', 'b', 't'
   * @param {Number} val
   */
  getFormatSizeIndex(val: number): number {
    const k = 1000
    const i = Math.floor(Math.log(Math.abs(val)) / Math.log(k))
    return i
  },

  /**
   * convert seze in joule to kJ, MJ, GJ, ...
   * @param {Number} joule - joule 크기
   * @param {Number} decimals - 표현할 소수점 자리수
   * @param {Boolean} removeUnit - 표시 단위 제거 여부
   * @param {Number} formatUnitIndex - 단위 number(1:J, 2:kJ, 3:GJ, 4:TJ, 5:PJ, 6:EJ, 7:ZJ, 8:YJ)
   */
  formatJoule(
    joules: number,
    decimals?: number,
    removeUnit?: boolean,
    formatUnitIndex?: number,
  ): string | number {
    if (joules == 0) {
      return removeUnit ? 0 : '0 J'
    }

    const reversNum = joules > 0 ? 1 : -1 //음수가 들어왔을 경우 log 계산식에서는 양수로 계산하고 return 시 음수로 보정하기 위함
    joules *= reversNum
    const k = 1000
    const dm = decimals === undefined || decimals <= 0 ? 0 : decimals || 3
    const sizes = ['J', 'kJ', 'MJ', 'GJ', 'TJ', 'PJ', 'EJ', 'ZJ', 'YJ']
    const i = formatUnitIndex ? formatUnitIndex : Math.floor(Math.log(joules) / Math.log(k))
    if (removeUnit) {
      return parseFloat((joules / Math.pow(k, i)).toFixed(dm)) * reversNum
    } else {
      return parseFloat((joules / Math.pow(k, i)).toFixed(dm)) * reversNum + ' ' + sizes[i]
    }
  },

  /**
   * 3자리 수마다 , 추가
   * @param {Number} num
   * @param {Boolean} zeroTrim 소수점 아래 맨 뒤 0 제거
   */
  addComma(val: string | number, zeroTrim = false): string {
    if (val == undefined) {
      return ''
    }
    const regexp = /\B(?=(\d{3})+(?!\d))/g
    const arr = val.toString().split('.')

    if (arr[0] !== undefined) {
      arr[0] = arr[0].replace(regexp, ',')
    }
    if (zeroTrim && arr[1]) {
      arr[1] = arr[1].replace(/0*$/g, '')
      return arr.join('.').replace(/\.*$/g, '')
    } else {
      return arr.join('.')
    }
  },

  downloadCSV(csv: string, filename: string): void {
    var csvFile: Blob
    var downloadLink: HTMLAnchorElement

    //한글 처리를 해주기 위해 BOM 추가하기
    const BOM = '\uFEFF'
    csv = BOM + csv

    csvFile = new Blob([csv], { type: 'text/csv' })
    downloadLink = document.createElement('a')
    downloadLink.download = filename
    downloadLink.href = window.URL.createObjectURL(csvFile)
    downloadLink.style.display = 'none'
    document.body.appendChild(downloadLink)
    downloadLink.click()
  },

  sortDesc(array: SortableObject[], target: string): void {
    array.sort(function (comp1, comp2) {
      var comp1UC = comp1[target] as string | number
      var comp2UC = comp2[target] as string | number

      if (comp1UC === comp2UC) return 0
      if (comp1UC === null || comp1UC === undefined) return 1
      if (comp2UC === null || comp2UC === undefined) return -1

      if (comp1UC < comp2UC) {
        return 1
      } else if (comp1UC > comp2UC) {
        return -1
      }
      return 0
    })
  },

  sortAsc(array: SortableObject[], target: string): void {
    array.sort(function (comp1, comp2) {
      var comp1UC = comp1[target] as string | number
      var comp2UC = comp2[target] as string | number

      if (comp1UC === comp2UC) return 0
      if (comp1UC === null || comp1UC === undefined) return -1
      if (comp2UC === null || comp2UC === undefined) return 1

      if (comp1UC > comp2UC) {
        return 1
      } else if (comp1UC < comp2UC) {
        return -1
      }
      return 0
    })
  },

  getValue<T extends object>(item: T, headerValue: string): unknown {
    return Reflect.get(item, headerValue) ?? ''
  },

  isObject(item: unknown): boolean {
    return (item && typeof item === 'object' && !Array.isArray(item)) as boolean
  },

  /**
   * convert size in watt to kW, MW, GW, ...
   * @param {Number} watt - 와트 수
   * @param {Number} decimals - 표현할 소수점 자리수
   * @param {Boolean} removeUnit - 표시 단위 제거 여부
   * @param {Number} formatUnitIndex - 단위 number(0:W, 1:KW, 2:MW, 3:GW, 4:TW, 5:PW)
   */
  formatPowerWatt(
    watt?: number,
    decimals?: number,
    removeUnit?: boolean,
    formatUnitIndex?: number,
  ): string | number {
    if (!watt) {
      return removeUnit ? 0 : '0 W'
    }

    const reversNum = watt > 0 ? 1 : -1
    const absWatt = Math.abs(watt)
    const k = 1000
    const sizes = ['W', 'KW', 'MW', 'GW', 'TW', 'PW']

    let i =
      formatUnitIndex !== undefined ? formatUnitIndex : Math.floor(Math.log(absWatt) / Math.log(k))
    i = Math.min(Math.max(0, i), sizes.length - 1)

    // 사용자가 소수점 자리를 지정하지 않은 경우: W 단위는 0, 그 이상은 1을 기본값으로 사용
    const dm = decimals !== undefined ? decimals : i === 0 ? 0 : 1

    const calcValue = parseFloat((absWatt / Math.pow(k, i)).toFixed(dm)) * reversNum

    if (removeUnit) {
      return calcValue
    } else {
      // 3자리마다 콤마 추가 및 불필요한 소수점(.0) 표시 제거
      return calcValue.toLocaleString('en-US', { maximumFractionDigits: dm }) + ' ' + sizes[i]
    }
  },

  /**
   * 가속 전압을 표시 규격에 맞게 포맷팅 (DB tb_data_metadata.voltage 기본 단위는 kV)
   * - 단위 없는 숫자는 kV 값으로 본다 (ex: 300 -> "300 kV", 120 -> "120 kV")
   * - 이미 "kV" 또는 "V" 단위가 붙어있는 문자열도 정밀 판별하여 처리
   *   ("V" 값도 항상 kV로 바꿔 표시, ex: "300000 V" -> "300 kV", "120100 V" -> "120.1 kV")
   * - 값이 없거나 0 이하면 "—"
   */
  formatVoltage(val: number | string | null | undefined): string {
    if (val === null || val === undefined || val === '') return '—'
    const str = String(val).trim()
    if (!str || str === '-' || str === '—') return '—'

    // 이미 "kV" 단위가 붙은 경우 (ex: "300kV", "300 kV")
    if (/^[0-9.]+\s*kv$/i.test(str)) {
      const num = parseFloat(str.replace(/[^0-9.]/g, ''))
      return isNaN(num) ? str : `${num} kV`
    }

    // 이미 "V" 단위가 붙은 경우 (ex: "300000 V", "120100 V"): kV로 바꾼다
    if (/^[0-9.]+\s*v$/i.test(str)) {
      const num = parseFloat(str.replace(/[^0-9.]/g, ''))
      return isNaN(num) ? str : `${num / 1000} kV`
    }

    // 순수 숫자인 경우: kV 값
    const num = Number(str)
    if (isNaN(num)) {
      return str
    }
    if (num <= 0) {
      return '—'
    }
    return `${num} kV`
  },

  /**
   * 노출 시간을 초 단위로 표시 (DB tb_data_metadata.exposure_time_s 는 단위 없는 초 값, ex: "0.6384")
   * - 숫자면 " s"를 붙인다 (ex: "1.2" -> "1.2 s")
   * - 이미 단위가 붙은 값은 그대로 둔다
   * - 값이 없으면 빈 문자열 (화면마다 쓰는 대체 표기를 그대로 쓰게 한다)
   */
  formatExposureTime(val: number | string | null | undefined): string {
    if (val === null || val === undefined) return ''
    const str = String(val).trim()
    if (!str) return ''
    return isNaN(Number(str)) ? str : `${str} s`
  },

  /**
   * 서버 날짜·일시 값을 Date로 바꾼다. 서버는 일시를 UTC로 보내므로(예: 2026-10-23T01:46:25Z)
   * Date로 바꾼 뒤 로컬(한국) 시간으로 표시해야 한다. 읽을 수 없으면 null.
   * - 'YYYY-MM-DD'·'YYYY.MM.DD' (날짜만): 그 날짜의 로컬 자정
   * - 'YYYY-MM-DD HH:mm[:ss]' (오프셋 없이 이미 화면용으로 바꾼 값): 로컬 시각
   * - PostgreSQL 문자열 '2026-10-23 01:46:25.372145+00': ISO 형식으로 고쳐서 읽음
   */
  parseDate(value?: string | Date | null): Date | null {
    if (value instanceof Date) return isNaN(value.getTime()) ? null : value
    const str = value == null ? '' : String(value).trim()
    if (!str || str === '-' || str === 'null' || str === 'undefined') return null

    const dateOnly = str.match(/^(\d{4})[-.](\d{2})[-.](\d{2})$/)
    if (dateOnly) return new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))

    const local = str.match(/^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2})(?::(\d{2}))?$/)
    if (local) {
      return new Date(Number(local[1]), Number(local[2]) - 1, Number(local[3]), Number(local[4]), Number(local[5]), Number(local[6] ?? 0))
    }

    const iso = str
      .replace(' ', 'T')
      .replace(/(\.\d{3})\d+/, '$1') // 소수점 아래 6자리 → 3자리 (브라우저 호환)
      .replace(/([+-]\d{2})$/, '$1:00') // '+00' → '+00:00'
    const d = new Date(iso)
    return isNaN(d.getTime()) ? null : d
  },

  /**
   * 날짜를 'YYYY-MM-DD'(로컬)로 표시. 값이 없으면 emptyText, 읽을 수 없으면 원래 값.
   */
  formatDate(value?: string | Date | null, emptyText = '-'): string {
    const d = commonUtil.parseDate(value)
    if (!d) return commonUtil.isBlankDate(value) ? emptyText : String(value)
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  },

  /**
   * 일시를 'YYYY-MM-DD HH:mm:ss'(로컬)로 표시. 값이 없으면 emptyText, 읽을 수 없으면 원래 값.
   */
  formatDateTime(value?: string | Date | null, emptyText = '-'): string {
    const d = commonUtil.parseDate(value)
    if (!d) return commonUtil.isBlankDate(value) ? emptyText : String(value)
    const pad = (n: number) => String(n).padStart(2, '0')
    return `${commonUtil.formatDate(d)} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
  },

  // 날짜 값이 비어 있는지(없음·빈 문자열·'-'·'null'·'undefined')
  isBlankDate(value?: string | Date | null): boolean {
    if (value instanceof Date) return isNaN(value.getTime())
    const str = value == null ? '' : String(value).trim()
    return !str || str === '-' || str === 'null' || str === 'undefined'
  },
}

export const parseDate = commonUtil.parseDate
export const formatDate = commonUtil.formatDate
export const formatDateTime = commonUtil.formatDateTime
export const formatVoltage = commonUtil.formatVoltage
export const formatExposureTime = commonUtil.formatExposureTime
export const formatBytes = commonUtil.formatBytes
export default commonUtil
